import React, { useMemo } from 'react';
import { SingleSelect } from '../../components/forms/SingleSelect';
import { useTranslation } from '../../providers/TranslationProvider';
import { useThemeColors } from '../../providers/ThemeProvider';
import { AutomationStep } from '../types/automation';

interface StepReferenceSelectorProps {
  steps: Array<AutomationStep>;
  currentStepId?: number;
  value?: number | null;
  onChange: (stepId: number | null) => void;
  placeholder?: string;
  excludeStepIds?: Array<number>;
  label?: string;
}

export const StepReferenceSelector: React.FC<StepReferenceSelectorProps> = ({
  steps,
  currentStepId,
  value,
  onChange,
  placeholder,
  excludeStepIds = [],
  label
}) => {
  const { t } = useTranslation();
  const colors = useThemeColors();

  // Build options from steps
  const options = useMemo(() => {
    // Get the position/order of each step for numbering
    const stepPositions = new Map<number, number>();
    steps.forEach((step, index) => {
      if (step.id != null) {
        stepPositions.set(step.id, index + 1);
      }
    });

    return steps
      .filter((step) => {
        // Exclude start step
        if (step.stepType === 'start') {
          return false;
        }

        // Exclude current step
        if (currentStepId != null && step.id === currentStepId) {
          return false;
        }

        // Exclude explicitly excluded steps
        if (step.id != null && excludeStepIds.includes(step.id)) {
          return false;
        }

        return true;
      })
      .map((step) => {
        const position = step.id != null ? stepPositions.get(step.id) : undefined;
        const label = position != null
          ? `#${position} - ${step.stepName}`
          : step.stepName;

        return {
          value: step.id?.toString() ?? '',
          label: t(label)
        };
      });
  }, [steps, currentStepId, excludeStepIds, t]);

  const handleChange = (selectedValue: string) => {
    if (selectedValue == null || selectedValue === '') {
      onChange(null);
    } else {
      const stepId = parseInt(selectedValue, 10);
      onChange(isNaN(stepId) ? null : stepId);
    }
  };

  return (
    <div>
      {label != null && (
        <label className={`text-[13px] mb-1 block ${colors.classes.textSecondary}`}>
          {t(label)}
        </label>
      )}
      <SingleSelect
        name="stepReference"
        options={options}
        value={value?.toString() ?? null}
        onChange={handleChange}
        placeholder={placeholder ?? t('Select a step...')}
        allowClear={true}
        fullWidth={true}
      />
    </div>
  );
};
